import { cn } from '@talentbabu/ui';
import { ChevronDown } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

/** Highlight words in a heading (orange → gold shimmer). */
export function GradientText({ children }: { children: ReactNode }) {
  return <span className="site-gradient-text">{children}</span>;
}

/** Small rounded label above a heading. */
export function Eyebrow({ icon, children, className }: { icon?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-full border border-brand-300/60 bg-brand-50 px-3.5 py-1.5 text-xs font-semibold tracking-wide text-brand-700',
        className,
      )}
    >
      {icon}
      {children}
    </span>
  );
}

/** Centred section heading: optional eyebrow, title (with GradientText inside), subtitle. */
export function SiteSectionHeading({
  eyebrow,
  title,
  subtitle,
  align = 'center',
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  align?: 'center' | 'left';
  className?: string;
}) {
  return (
    <div
      className={cn(align === 'center' ? 'mx-auto max-w-3xl text-center' : 'max-w-3xl', 'mb-10 sm:mb-12', className)}
    >
      {eyebrow && <div className="mb-4">{eyebrow}</div>}
      <h2 className="text-3xl leading-[1.1] font-bold text-slate-900 sm:text-[44px]">{title}</h2>
      {subtitle && <p className="mt-4 text-lg leading-relaxed text-slate-500">{subtitle}</p>}
    </div>
  );
}

/** Accessible disclosure list (one open at a time). */
export function Faq({ items }: { items: { q: string; a: ReactNode }[] }) {
  const [open, setOpen] = useState<number | null>(0);
  const base = useId();
  return (
    <div className="mx-auto max-w-3xl space-y-3">
      {items.map((item, i) => {
        const isOpen = open === i;
        return (
          <div key={item.q} className="rounded-xl border border-slate-200 bg-white">
            <h3>
              <button
                type="button"
                id={`${base}-q${i}`}
                aria-expanded={isOpen}
                aria-controls={`${base}-a${i}`}
                onClick={() => setOpen(isOpen ? null : i)}
                className="flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-4 text-left font-semibold text-slate-900"
              >
                {item.q}
                <ChevronDown
                  className={cn('h-5 w-5 shrink-0 text-slate-400 transition-transform', isOpen && 'rotate-180')}
                  aria-hidden
                />
              </button>
            </h3>
            <div
              id={`${base}-a${i}`}
              role="region"
              aria-labelledby={`${base}-q${i}`}
              hidden={!isOpen}
              className="px-5 pb-5 leading-relaxed text-slate-500"
            >
              {item.a}
            </div>
          </div>
        );
      })}
    </div>
  );
}
